import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Drawer, Typography, Input, Space, Button, Select, message } from 'antd';
import { getTranslatedString } from 'helpers/i18n';
import React, { useContext } from 'react';
import { ApplicationUIContext, Form } from '@yuhuinc/ui-library';
import { CurrentApplicationContext } from '../../ApplicationOverview';
import { getNonOccupantApplicants } from '../../common/helpers';
import { apiPost } from 'helpers/ApiHelper';
import { onError } from '../../common/ErrorHandling';
const { Paragraph } = Typography;
const { TextArea } = Input;

const RequestAdditionalDetailsDrawer = ({ drawerState }) => (
  <Drawer
    title={getTranslatedString(
      'landlordPortal.applicationManagement.applicationOverview.review.actions.requestAdditionalDetails'
    )}
    visible={drawerState.isVisible}
    closable
    destroyOnClose
    onClose={drawerState.close}
  >
    <RequestAdditionalDetailsForm drawerState={drawerState} />
  </Drawer>
);

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const RequestAdditionalDetailsForm = ({ drawerState }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const currentApplication = useContext(CurrentApplicationContext);
  const queryClient = useQueryClient();

  const requestAdditionalDetailsMutation = useMutation({
    mutationFn: (data) => {
      return apiPost(
        `/landlords/application_management/applications/${currentApplication.id}/applicants/request_additional_details`,
        data
      );
    },
    onSuccess() {
      // Update message once we get feedback from design team
      message.success('Additional details requested');
      drawerState.close();
    },
    onError,
    onSettled() {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const allApplicantsAndGuarantorsOption = {
    label: getTranslatedString(
      'landlordPortal.applicationManagement.applicationOverview.review.actions.requestAdditionalDetails.allApplicantsAndGuarantors'
    ),
    value: 'all',
  };
  const nonOccupantApplicants = getNonOccupantApplicants(currentApplication)?.map((applicant) => ({
    label: applicant.full_name,
    value: applicant.id,
  }));

  const showAllApplicantsOption = !nonOccupantApplicants?.some((applicant) =>
    values?.cotenant_ids?.includes(applicant.value)
  );
  const showSpecificApplicantsOptions = !values?.cotenant_ids?.includes('all');

  const options = [
    ...(showAllApplicantsOption ? [allApplicantsAndGuarantorsOption] : []),
    ...(showSpecificApplicantsOptions ? nonOccupantApplicants : []),
  ];

  const onFinish = (values) => {
    if (values?.cotenant_ids?.includes(allApplicantsAndGuarantorsOption.value)) {
      requestAdditionalDetailsMutation.mutate({
        ...values,
        cotenant_ids: nonOccupantApplicants.map((a) => a.value),
      });
    } else {
      requestAdditionalDetailsMutation.mutate(values);
    }
  };

  return (
    <Form form={form} layout="vertical" size={isMobile && 'large'} gap="middle" onFinish={onFinish}>
      <Form.Item
        label={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.review.actions.requestAdditionalDetails.selectApplicants'
        )}
        name="cotenant_ids"
        rules={[requiredField]}
      >
        <Select
          mode="multiple"
          options={options}
          filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
        />
      </Form.Item>

      <div>
        <Form.Item label="Notes" name="notes" rules={[requiredField]}>
          <TextArea />
        </Form.Item>

        <Paragraph>
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.review.actions.requestAdditionalDetails.thisCommentIsVisibleToAllApplicants'
          )}
        </Paragraph>
      </div>

      <Space>
        <Button
          type="primary"
          htmlType="submit"
          disabled={!values?.notes || !values?.cotenant_ids?.length}
          loading={requestAdditionalDetailsMutation.isLoading}
        >
          {getTranslatedString('submit')}
        </Button>
      </Space>
    </Form>
  );
};

export default RequestAdditionalDetailsDrawer;
